import { useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, ReceiptIcon } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { useCurrentBusiness } from '@/hooks/use-current-business';
import { useRole } from '@/hooks/use-role';
import {
  businessInvoicesQueryKey,
  createJobInvoice,
  listBusinessInvoices,
  type ListBusinessInvoicesFilters,
} from '@/lib/api/invoices';

import { AddInvoiceModal } from './add-invoice-modal';
import { PAGE_SIZE } from './helpers';
import { InvoiceDetailModal } from './invoice-detail-modal';
import { InvoicesGrid } from './invoices-grid';
import type { Invoice, InvoiceStatusFilter, NewInvoiceSubmission } from './types';

const STATUS_TABS: InvoiceStatusFilter[] = ['all', 'unpaid', 'pending_confirmation', 'paid'];

export function Invoices() {
  const { t } = useLanguage();
  const business = useCurrentBusiness();
  const businessId = business?.business_id;
  const { isOwner } = useRole();

  const [status, setStatus] = useState<InvoiceStatusFilter>('all');
  const [page, setPage] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [active, setActive] = useState<Invoice | null>(null);
  const [autoShareOnOpen, setAutoShareOnOpen] = useState(false);

  const filters = useMemo<ListBusinessInvoicesFilters>(
    () => (status === 'all' ? {} : { status }),
    [status],
  );

  const invoicesQuery = useQuery({
    queryKey: businessInvoicesQueryKey(businessId ?? '', filters),
    queryFn: () => listBusinessInvoices(businessId!, filters),
    enabled: Boolean(businessId) && isOwner,
  });

  const createMutation = useMutation({
    mutationFn: (s: NewInvoiceSubmission) =>
      createJobInvoice(businessId!, s.jobId, {
        total_amount: s.total_amount,
        payment_method_ids: s.payment_method_ids,
        subtotal: s.subtotal,
        tax: s.tax,
        discount: s.discount,
        due_date: s.due_date,
      }),
    meta: { successMessage: t.invoiceCreated },
    onSuccess: (invoice) => {
      setIsAdding(false);
      setAutoShareOnOpen(true);
      setActive(invoice);
    },
  });

  const invoices = invoicesQuery.data ?? [];
  const totalPages = Math.max(1, Math.ceil(invoices.length / PAGE_SIZE));
  const paginated = invoices.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleStatus(next: InvoiceStatusFilter) {
    setStatus(next);
    setPage(1);
  }

  function statusLabel(s: InvoiceStatusFilter): string {
    switch (s) {
      case 'unpaid':
        return t.invoiceStatusUnpaid;
      case 'pending_confirmation':
        return t.invoiceStatusPendingConfirmation;
      case 'paid':
        return t.invoiceStatusPaid;
      default:
        return t.allStatuses;
    }
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <ReceiptIcon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold leading-tight">{t.invoicesTitle}</h1>
            <p className="text-xs text-muted-foreground">
              {invoices.length} {t.invoicesCount}
            </p>
          </div>
        </div>
        {isOwner && (
          <Button
            size="sm"
            className="gap-1.5"
            data-testid="add-invoice"
            onClick={() => setIsAdding(true)}
          >
            <PlusIcon className="w-4 h-4" />
            {t.addInvoice}
          </Button>
        )}
      </div>

      {!isOwner ? (
        <p className="text-sm text-muted-foreground py-10 text-center">
          {t.invoicesOwnerOnly}
        </p>
      ) : (
        <>
          <div className="flex gap-1 border-b mb-5">
            {STATUS_TABS.map((s) => (
              <button
                key={s}
                onClick={() => handleStatus(s)}
                className={[
                  'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                  status === s
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                ].join(' ')}
                data-testid={`filter-status-${s}`}
              >
                {statusLabel(s)}
              </button>
            ))}
          </div>

          {invoicesQuery.isPending ? (
            <p className="text-sm text-muted-foreground py-10 text-center">{t.loading}</p>
          ) : (
            <InvoicesGrid
              invoices={paginated}
              onSelect={(invoice) => {
                setAutoShareOnOpen(false);
                setActive(invoice);
              }}
            />
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="gap-1.5"
                data-testid="prev-page"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                {t.previous}
              </Button>
              <span className="text-xs text-muted-foreground">
                {page} {t.pageOf} {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="gap-1.5"
                data-testid="next-page"
              >
                {t.nextPage}
                <ChevronRightIcon className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {isAdding && businessId && (
        <AddInvoiceModal
          businessId={businessId}
          onClose={() => setIsAdding(false)}
          onSave={(s) => createMutation.mutate(s)}
          isSaving={createMutation.isPending}
        />
      )}

      {active && businessId && (
        <InvoiceDetailModal
          businessId={businessId}
          jobId={active.job_id}
          invoiceId={active.id}
          canManage={isOwner}
          autoShareLink={autoShareOnOpen}
          onClose={() => {
            setActive(null);
            setAutoShareOnOpen(false);
          }}
        />
      )}
    </div>
  );
}
